import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'

export type ClientErrorKind = 'render' | 'window' | 'promise' | 'api' | 'network'

export type ClientErrorEntry = {
  id: string
  fingerprint: string
  kind: ClientErrorKind
  message: string
  stack: string | null
  route: string | null
  apiPath: string | null
  apiStatus: number | null
  bundle: string | null
  userEmail: string | null
  userAgent: string | null
  sessionRef: string | null
  createdAt: string
}

export type ClientErrorGroup = {
  fingerprint: string
  kind: ClientErrorKind
  count: number
  firstSeen: string
  lastSeen: string
  sample: string
}

export type ClientErrorFilters = {
  kind?: ClientErrorKind | null
  fingerprint?: string | null
  dateFrom?: string | null
  dateTo?: string | null
}

type Page = { items: ClientErrorEntry[]; nextCursor: string | null }

export function useClientErrors(filters: ClientErrorFilters) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: ['client-errors', filters],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const qs = buildQuery({
        kind: filters.kind ?? undefined,
        fingerprint: filters.fingerprint ?? undefined,
        date_from: filters.dateFrom ?? undefined,
        date_to: filters.dateTo ?? undefined,
        cursor: pageParam ?? undefined,
        limit: 50,
      })
      return api<Page>(`/client-errors${qs}`)
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export function useClientErrorSummary() {
  const api = useApi()
  return useQuery({
    queryKey: ['client-errors', 'summary'],
    queryFn: () => api<{ groups: ClientErrorGroup[] }>('/client-errors/summary'),
  })
}
